// src/api/mappers/antilossMapper.js — VIRAJ SYSTEM v9.2
//
// Канон: ARBITRAGE_MODE_SPEC v9.2 — "все данные проходят через antilossMapper.js"
// Назначение: нормализует данные из /api/antiloss/actions и /api/antiloss/prevented
// для использования в ArbitragePage, ActionsTimeline, FinalResultCTA.

const HUMAN_ACTION = {
  reduce_spend:    "Reduced spend",
  limit_budget:    "Budget cap enforced",
  pause_campaigns: "Campaigns paused",
  safe_mode:       "Safe mode activated",
  monitor:         "Monitoring enhanced",
};

/**
 * Нормализует одно auto_action.
 *
 * @param {object} action
 * @returns {{
 *   id: number,
 *   timestamp: number,    — unix sec
 *   date: string,         — YYYY-MM-DD
 *   dateLabel: string,    — "15 Mar"
 *   action_type: string,
 *   action_label: string, — human readable
 *   prevented_loss: number,
 *   risk_score: number,
 *   context: object,
 * }}
 */
export function mapAction(action) {
  if (!action) return null;

  const ts = Number(action.timestamp) || 0;
  const d  = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);

  const date      = d.toISOString().slice(0, 10);
  const dateLabel = d.toLocaleString("en-GB", { day: "2-digit", month: "short" });

  let context = {};
  try { context = JSON.parse(action.context ?? "{}"); } catch { /* pass */ }

  return {
    id:             action.id ?? 0,
    timestamp:      ts,
    date,
    dateLabel,
    action_type:    action.action_type   ?? "monitor",
    action_label:   HUMAN_ACTION[action.action_type] ?? "Engine action",
    prevented_loss: Number(action.prevented_loss)    || 0,
    risk_score:     Number(action.risk_score)         || 0,
    context,
  };
}

/**
 * Нормализует массив actions.
 *
 * @param {object[]} items
 * @returns {object[]}
 */
export function mapActions(items) {
  if (!Array.isArray(items)) return [];
  return items.map(mapAction).filter(Boolean);
}

/**
 * Суммирует prevented_loss из actions.
 *
 * @param {object[]} mappedActions — результат mapActions()
 * @returns {number}
 */
export function totalPreventedLoss(mappedActions) {
  return mappedActions.reduce((sum, a) => sum + a.prevented_loss, 0);
}

/**
 * Находит первое действие (Moment of Intervention).
 *
 * @param {object[]} mappedActions
 * @returns {object|null}
 */
export function getMomentOfIntervention(mappedActions) {
  if (!mappedActions.length) return null;
  return [...mappedActions].sort((a, b) => a.timestamp - b.timestamp)[0];
}

/**
 * Группирует actions по типу для Timeline.
 * Возвращает до maxTypes уникальных типов с суммарным saved и количеством.
 *
 * @param {object[]} mappedActions
 * @param {number}   maxTypes
 * @returns {{type, label, count, totalSaved, dateLabel}[]}
 */
export function groupActionsByType(mappedActions, maxTypes = 3) {
  const byType = {};

  [...mappedActions]
    .sort((a, b) => a.timestamp - b.timestamp)
    .forEach(a => {
      const k = a.action_type;
      if (!byType[k]) {
        byType[k] = {
          type:       k,
          label:      a.action_label,
          count:      0,
          totalSaved: 0,
          dateLabel:  a.dateLabel,
        };
      }
      byType[k].count++;
      byType[k].totalSaved += a.prevented_loss;
    });

  return Object.values(byType).slice(0, maxTypes);
}
